import { useMemo, useState } from 'react';
import {
  describeRepeat,
  isAdmin,
  MAX_REMINDER_AHEAD_MS,
  MAX_REMINDER_TEXT,
  MIN_REMINDER_LEAD_MS,
  type Reminder,
  type ReminderHistoryEntry,
  type ReminderRepeatKind,
  type RepeatRule,
} from '@avslack/shared';
import {
  useAckReminder,
  useCancelReminder,
  useCreateReminder,
  useReminderHistory,
  useReminders,
  useUpdateReminder,
} from '../data/reminders';
import { useUsers } from '../data/users';
import { useGroups } from '../data/workspace';
import { useSession } from '../lib/session';
import { Dialog, useAnimatedClose } from './Dialog';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { RepeatPicker } from './RepeatPicker';
import { useConfirm } from './Confirm';

/**
 * Emlekeztetok: lista es szerkesztes.
 *
 * Ket resze van. Felul a MAR BEALLITOTT emlekeztetok - enelkul nem lehetne
 * megnezni, mi van fuggoben, es egy elgepelt idopontot sem lehetne javitani.
 * Alul az urlap: uj emlekezteto, vagy a kivalasztott szerkesztese.
 *
 * **Admin mindenkiet latja.** Egy rossz idopontra allitott, egesz csapatnak
 * szolo emlekeztetot valakinek tudnia kell rendbe tenni. A jogot a SZERVER
 * ellenorzi - a `mind=1` sima felhasznalonal a sajat listat adja vissza.
 *
 * ─── Ket ful: Aktiv es Korabbiak ────────────────────────────────────────────
 *
 * Az "Aktiv" arrol szol, mi fog szolni; a "Korabbiak" arrol, mi szolt mar. A
 * ketto kulonbozo kerdes, es kulonbozo mozdulat tartozik hozzajuk -
 * szerkesztes az egyikhez, kipipalas a masikhoz. Egy listaban a ketto
 * osszefolyna: a mult heti, mar elintezett emlekezteto ott allna a holnapi
 * mellett, ugyanolyan sullyal.
 */

function toInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultWhen(): Date {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date;
}

export function ReminderDialog({
  onClose: closeNow,
  kezdoFul = 'aktiv',
}: {
  onClose: () => void;
  /** Melyik fulon nyiljon. Az oldalsav elozmeny-gombja a "korabbiak"-at keri. */
  kezdoFul?: 'aktiv' | 'korabbiak';
}) {
  const onClose = useAnimatedClose(closeNow);
  const session = useSession();
  const me = session.data?.user;
  const meId = me?.id ?? '';
  const admin = me ? isAdmin(me) : false;

  const { users, nameOf } = useUsers();
  const groups = useGroups().data ?? [];
  const [showAll, setShowAll] = useState(false);
  const list = useReminders(admin && showAll);

  const create = useCreateReminder();
  const update = useUpdateReminder();
  const cancel = useCancelReminder();
  const ack = useAckReminder();
  const confirm = useConfirm();

  const [ful, setFul] = useState<'aktiv' | 'korabbiak'>(kezdoFul);
  const elozmeny = useReminderHistory();

  /** Amit eppen szerkesztunk. `null` = uj emlekezteto. */
  const [editing, setEditing] = useState<Reminder | null>(null);

  const [text, setText] = useState('');
  const [when, setWhen] = useState(() => toInputValue(defaultWhen()));
  const [targets, setTargets] = useState<Set<string>>(() => new Set([meId]));
  const [groupTargets, setGroupTargets] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  /*
    AZ ISMETLODES.

    Harom mezo, mert harom kulon dontes: mi a ritmus, mely napokon, es meddig.
    A "meddig" URES is maradhat - a leggyakoribb eset az, hogy amig vissza nem
    vonjak, es egy kotelezo veghatar ezt tenne korulmenyesse.
  */
  const [repeatKind, setRepeatKind] = useState<ReminderRepeatKind>('none');
  const [repeatDays, setRepeatDays] = useState<Set<number>>(() => new Set());
  const [repeatUntil, setRepeatUntil] = useState('');

  /**
   * Melyik zonaban ertendo a "reggel 9".
   *
   * A sajat profilunkbol - ugyanabbol, amit a szerver is hasznal a
   * beallitaskor. Igy az elonezet ugyanazt az idopontot mutatja, amit kesobb
   * a kikuldes valasztani fog.
   */
  const timeZone = me?.timezone || 'Europe/Budapest';

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  const date = new Date(when);
  const valid = !Number.isNaN(date.getTime());
  const ahead = valid ? date.getTime() - Date.now() : 0;

  /*
    A HIANYZO SZOVEG nem hiba, amig senki nem irt semmit.

    Ez a sor korabban pirosan, figyelmeztetesként jelent meg - ugy is, hogy a
    parbeszed epp csak kinyilt, es ugy is KOZVETLENUL EGY SIKERES MENTES UTAN:
    a mentes uriti a mezot, a szoveg pedig azonnal visszaszolt, hogy "Írd le,
    mire emlékeztessen". A felhasznalo epp letrehozott egy emlekeztetot, es
    valasznak egy piros hibauzenetet kapott.

    Ures mezonel tehat hallgatunk: a helyorzo szoveg es a letiltott gomb
    egyutt amugy is elmondja, mi hianyzik.
  */
  const erintetlen = !text.trim();

  const problem = !text.trim()
    ? 'Írd le, mire emlékeztessen.'
    : !valid
      ? 'Ez az időpont nem értelmezhető.'
      : ahead < MIN_REMINDER_LEAD_MS
        ? 'Legalább egy perccel későbbre kérj emlékeztetőt.'
        : ahead > MAX_REMINDER_AHEAD_MS
          ? `Legfeljebb ${Math.round(MAX_REMINDER_AHEAD_MS / 86_400_000)} nappal előre lehet.`
          : targets.size === 0 && groupTargets.size === 0
            ? 'Legalább egy címzett kell.'
            : null;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const active = users.filter((user) => user.isActive !== false);
    if (!term) return active;
    return active.filter((user) => nameOf(user).toLowerCase().includes(term));
  }, [users, search, nameOf]);

  const reset = () => {
    setEditing(null);
    setText('');
    setWhen(toInputValue(defaultWhen()));
    setTargets(new Set([meId]));
    setGroupTargets(new Set());
    setRepeatKind('none');
    setRepeatDays(new Set());
    setRepeatUntil('');
    setError(null);
  };

  const startEdit = (reminder: Reminder) => {
    setEditing(reminder);
    setText(reminder.text);
    setWhen(toInputValue(new Date(reminder.remindAt)));
    /*
      A csoportbol EREDO cimzetteket levonjuk a nevsorbol.

      A `targetIds` mar kibontva jon (a csoport tagjaival egyutt); ha
      valtozatlanul visszairnank, a szerkesztes A MAI taglistat rogzitene
      nevre szolo cimzettkent - es a kesobb belepok kimaradnanak.
    */
    const csoportTagok = new Set(
      reminder.groupIds.flatMap((id) => groups.find((g) => g.id === id)?.memberIds ?? []),
    );
    setTargets(new Set(reminder.targetIds.filter((id) => !csoportTagok.has(id))));
    setGroupTargets(new Set(reminder.groupIds));
    setRepeatKind(reminder.repeat.kind);
    setRepeatDays(new Set(reminder.repeat.days));
    setRepeatUntil(reminder.repeat.until ? reminder.repeat.until.slice(0, 10) : '');
    setError(null);
  };

  /*
    A SZABALY, ahogy a szerver is latni fogja.

    A horgony a most beallitott idopont: a havi ismetlodes ebbol veszi a nap
    sorszamat, es az elonezet is ezzel szamol - igy amit a felhasznalo lat,
    az ugyanaz, ami menteskor rogzul.
  */
  const rule: RepeatRule = {
    kind: repeatKind,
    days: [...repeatDays],
    until: repeatUntil ? new Date(`${repeatUntil}T23:59`).toISOString() : null,
    anchor: valid ? date.toISOString() : null,
    timeZone,
  };

  const toggleNap = (nap: number) =>
    setRepeatDays((current) => {
      const next = new Set(current);
      if (next.has(nap)) next.delete(nap);
      else next.add(nap);
      return next;
    });

  const toggleGroup = (id: string) =>
    setGroupTargets((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggle = (id: string) =>
    setTargets((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    if (problem) return;
    setError(null);

    /*
      AZ ENGEDELYT ITT kerjuk el, nem a lejaratkor.

      Ez a termeszetes pillanat: a felhasznalo epp most mondta ki, hogy
      ertesitest szeretne. A lejaratkor kerdezni mar keso - addigra az ablak
      lehet, hogy a hatterben van, es a kerdes eszreveletlen maradna.
    */
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        setPermission(await Notification.requestPermission());
      } catch {
        // Az engedely hianya nem akadalya az emlekezteto letrehozasanak.
      }
    }

    try {
      /*
        AZ ISMETLODES akkor is megy, ha `none`.

        Kulonben egy sorozatot nem lehetne visszaalakitani egyszerive: a
        hianyzo mezot a szerver "ne nyulj hozza"-kent olvassa, es a heti
        ritmus csendben megmaradna.
      */
      const repeat = {
        kind: repeatKind,
        days: [...repeatDays],
        until: rule.until,
      };

      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          text: text.trim(),
          remindAt: date.toISOString(),
          targetIds: [...targets],
          groupIds: [...groupTargets],
          repeat,
        });
      } else {
        await create.mutateAsync({
          text: text.trim(),
          remindAt: date.toISOString(),
          targetIds: [...targets],
          groupIds: [...groupTargets],
          repeat,
        });
      }
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Az emlékeztető mentése nem sikerült.');
    }
  };

  const busy = create.isPending || update.isPending;
  const reminders = list.data ?? [];

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Emlékeztetők"
      description="Megadott időpontban felugrik – akkor is, ha némítva vagy."
      width="lg"
    >
      {/*
        KET FUL: mi fog szolni, es mi szolt mar.

        Kulonbozo kerdes, kulonbozo mozdulat - amott szerkesztes, itt
        kipipalas. Egy listaban a mult heti, mar elintezett emlekezteto a
        holnapi mellett allna, ugyanolyan sullyal.
      */}
      <div
        role="tablist"
        aria-label="Emlékeztetők"
        className="mb-4 flex gap-1 border-b border-border-soft"
        onKeyDown={(event) => {
          // Ket ful: a NYIL barmelyik iranyba a masikra valt (WAI-ARIA tab minta).
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const next = ful === 'aktiv' ? 'korabbiak' : 'aktiv';
          setFul(next);
          document.getElementById(`emlekezteto-ful-${next}`)?.focus();
        }}
      >
        {(
          [
            ['aktiv', 'Aktív'],
            ['korabbiak', 'Korábbiak'],
          ] as const
        ).map(([ertek, cimke]) => (
          <button
            key={ertek}
            id={`emlekezteto-ful-${ertek}`}
            type="button"
            role="tab"
            aria-selected={ful === ertek}
            aria-controls="emlekezteto-panel"
            tabIndex={ful === ertek ? 0 : -1}
            onClick={() => setFul(ertek)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              ful === ertek
                ? 'border-accent font-semibold text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {cimke}
            {ertek === 'korabbiak' && (elozmeny.data?.unacked ?? 0) > 0 && (
              <span className="ml-1.5 rounded-pill bg-alert px-1.5 py-px text-2xs font-semibold text-white">
                {elozmeny.data?.unacked}
              </span>
            )}
          </button>
        ))}
      </div>

      <div id="emlekezteto-panel" role="tabpanel" aria-labelledby={`emlekezteto-ful-${ful}`}>
        {ful === 'korabbiak' ? (
          <ElozmenyLista
            entries={elozmeny.data?.entries ?? []}
            betolt={elozmeny.isPending}
            meId={meId}
            nameOf={nameOf}
            onAck={(deliveryId, acked) => ack.mutate({ deliveryId, acked })}
          />
        ) : (
          <>
            {/* ─── A fuggoben levok ────────────────────────────────────────────── */}
            <div className="mb-5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                  Aktív emlékeztetők ({reminders.length})
                </span>
                {admin && (
                  <label className="flex cursor-pointer items-center gap-1.5 text-2xs text-ink-muted">
                    <input
                      type="checkbox"
                      checked={showAll}
                      onChange={(event) => setShowAll(event.target.checked)}
                      className="accent-[var(--accent)]"
                    />
                    Mindenkié
                  </label>
                )}
              </div>

              {reminders.length === 0 ? (
                <p className="rounded-control border border-border-soft px-3 py-2.5 text-xs text-ink-faint">
                  Nincs beállított emlékeztető.
                </p>
              ) : (
                <ul className="max-h-52 space-y-1 overflow-y-auto">
                  {reminders.map((reminder) => (
                    <li
                      key={reminder.id}
                      className={`flex items-start gap-2 rounded-control border px-2.5 py-2 ${
                        editing?.id === reminder.id
                          ? 'border-accent/50 bg-accent/5'
                          : 'border-border-soft'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{reminder.text}</p>
                        <p className="mt-0.5 text-2xs text-ink-faint">
                          {new Date(reminder.remindAt).toLocaleString('hu-HU', {
                            month: 'short',
                            day: 'numeric',
                            weekday: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {' · '}
                          {reminder.groupIds.length > 0
                            ? reminder.groupIds
                                .map((id) => `@${groups.find((g) => g.id === id)?.handle ?? '?'}`)
                                .join(', ')
                            : reminder.targetIds.length === 1 && reminder.targetIds[0] === meId
                              ? 'neked'
                              : `${reminder.targetIds.length} címzett`}
                          {reminder.creatorId !== meId && ` · ${nameOf(reminder.creatorId)} kérte`}
                        </p>
                        {/*
                    Az ISMETLODES kiirasa nelkul egy sorozat pontosan ugy
                    nezne ki, mint egy egyszeri emlekezteto - es a
                    kovetkezo alkalom varatlanul jonne.
                  */}
                        {reminder.repeat.kind !== 'none' && (
                          <p className="mt-0.5 flex items-center gap-1 text-2xs text-accent">
                            <Icon name="ora" size="sm" />
                            {describeRepeat(new Date(reminder.remindAt), reminder.repeat)}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => startEdit(reminder)}
                        title="Szerkesztés"
                        className="rounded-control p-1 text-ink-muted hover:text-ink"
                      >
                        <Icon name="szerkesztes" size="sm" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void confirm({
                            title: 'Emlékeztető törlése',
                            body: 'Nem fog szólni. Ezt nem lehet visszavonni.',
                          }).then((ok) => {
                            if (!ok) return;
                            if (editing?.id === reminder.id) reset();
                            setError(null);
                            cancel.mutateAsync(reminder.id).catch(() => {
                              setError('Az emlékeztető törlése nem sikerült. Próbáld újra.');
                            });
                          });
                        }}
                        title="Törlés"
                        className="rounded-control p-1 text-ink-muted hover:text-danger"
                      >
                        <Icon name="torles" size="sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ─── Uj vagy szerkesztett ────────────────────────────────────────── */}
            <div className="border-t border-border-soft pt-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                  {editing ? 'Emlékeztető szerkesztése' : 'Új emlékeztető'}
                </span>
                {editing && (
                  <button
                    type="button"
                    onClick={reset}
                    className="text-2xs text-ink-muted underline hover:text-ink"
                  >
                    Mégsem szerkesztem
                  </button>
                )}
              </div>

              <textarea
                aria-label="Mire emlékeztessünk?"
                value={text}
                onChange={(event) => setText(event.target.value.slice(0, MAX_REMINDER_TEXT))}
                rows={2}
                placeholder="Pl. Heti riport elküldése"
                className="w-full resize-none rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
              />

              <input
                aria-label="Időpont"
                type="datetime-local"
                value={when}
                min={toInputValue(new Date(Date.now() + MIN_REMINDER_LEAD_MS))}
                max={toInputValue(new Date(Date.now() + MAX_REMINDER_AHEAD_MS))}
                onChange={(event) => setWhen(event.target.value)}
                className="mt-2 w-full rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
              />
              {valid && !problem && (
                <span className="mt-1 block text-xs text-ink-muted">
                  {date.toLocaleString('hu-HU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}

              {/*
          AZ ISMETLODES.

          A fenti idopont a KEZDES; innentol a ritmus dolga, mikor jon ujra.
          A napok csak a hetinel latszanak - egy "minden nap" mellett allo,
          kipipalhatatlan naplista csak azt kerdezne, mire valo.

          Az idopontot SZANDEKOSAN nem duplazzuk meg kulon ora-mezovel: a
          "reggel 9" onnan jon, ahova a kezdest allitottad. Egy masodik
          idopont-mezo mellett egy percen belul kiderulne, hogy a ketto
          egymasnak mond ellent.
        */}
              <RepeatPicker
                kind={repeatKind}
                onKind={setRepeatKind}
                days={repeatDays}
                onToggleDay={toggleNap}
                until={repeatUntil}
                onUntil={setRepeatUntil}
                start={valid ? date : null}
                rule={rule}
              />

              {groups.length > 0 && (
                <div className="mt-3">
                  <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                    Csoportnak
                  </span>
                  {/*
              A csoport NEM egy kenyelmi rovidites a nevsorra: a lejaratkori
              taglista szamit. Aki addig belep, azt is erinti - ezert mondjuk
              ki itt, hogy ne a neveket kelljen egyesevel kipipalni.
            */}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {groups.map((group) => {
                      const kivalasztva = groupTargets.has(group.id);
                      return (
                        <button
                          key={group.id}
                          type="button"
                          aria-pressed={kivalasztva}
                          onClick={() => toggleGroup(group.id)}
                          title={`${group.name} · ${group.memberIds.length} tag`}
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            kivalasztva
                              ? 'border-accent bg-accent/10 font-semibold text-accent'
                              : 'border-border-soft text-ink-muted hover:text-ink'
                          }`}
                        >
                          @{group.handle}
                          <span className="ml-1 text-2xs opacity-70">{group.memberIds.length}</span>
                        </button>
                      );
                    })}
                  </div>
                  {groupTargets.size > 0 && (
                    <p className="mt-1.5 text-2xs text-ink-faint">
                      A csoport MINDENKORI tagjai kapják meg – aki addig belép, az is.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-3">
                <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                  Rajtuk kívül kinek? ({targets.size})
                </span>
                <input
                  aria-label="Kolléga keresése"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Keresés név szerint"
                  className="mt-1.5 w-full rounded-control border border-border bg-surface-sunken px-3 py-1.5 text-sm"
                />
                <div className="mt-1.5 max-h-40 overflow-y-auto rounded-control border border-border-soft">
                  {visible.map((user) => (
                    <label
                      key={user.id}
                      className="flex cursor-pointer items-center gap-2.5 px-2.5 py-1.5 text-sm hover:bg-surface-hover"
                    >
                      <input
                        type="checkbox"
                        checked={targets.has(user.id)}
                        onChange={() => toggle(user.id)}
                        className="accent-[var(--accent)]"
                      />
                      <Avatar
                        userId={user.id}
                        name={nameOf(user)}
                        avatarUrl={user.avatarUrl}
                        size="sm"
                      />
                      <span className="truncate">
                        {nameOf(user)}
                        {user.id === meId && (
                          <span className="ml-1 text-2xs text-ink-faint">(te)</span>
                        )}
                      </span>
                    </label>
                  ))}
                  {visible.length === 0 && (
                    <p className="px-2.5 py-2 text-xs text-ink-faint">Nincs találat.</p>
                  )}
                </div>
              </div>

              {/*
          Ha a bongeszo TILTJA az ertesitest, mondjuk ki - kulonben a
          felhasznalo azt hinne, elromlott.
        */}
              {permission === 'denied' && (
                <p className="mt-2 text-xs text-ink-muted">
                  A böngésző letiltotta az értesítéseket, ezért az emlékeztető csak akkor lesz
                  látható, ha épp ezt az ablakot nézed. A cím melletti lakat ikonnál
                  engedélyezheted.
                </p>
              )}

              {/*
              A KETTO NEM UGYANAZ, ezert nem is ugy nez ki.

              Az `error` a szerver valasza: valami tenyleg elromlott - az
              piros. A `problem` csak annyit mond el, MIERT szurke meg a gomb;
              az utmutatas, nem hiba. Amig pirosan allt, minden felig kitoltott
              urlap ugy festett, mintha az alkalmazas hibat jelezne.
            */}
              {error ? (
                <p role="alert" className="mt-2 text-xs text-danger">
                  {error}
                </p>
              ) : problem && !erintetlen ? (
                <p className="mt-2 text-xs text-ink-muted">{problem}</p>
              ) : null}

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-control px-3 py-2 text-sm text-ink-muted hover:text-ink"
                >
                  Bezárás
                </button>
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={Boolean(problem) || busy}
                  className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink disabled:opacity-50"
                >
                  {busy ? 'Mentés…' : editing ? 'Mentés' : 'Emlékeztető kérése'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * AZ ELOZMENY: mi szolt nekem, es mikor.
 *
 * A legfrissebb elol - visszanezni mindig a legutobbit akarjuk. Minden sor
 * elott egy pipa: ez a "megvan". Kattintasra vissza is vonhato, mert a
 * felugro ablakon egy elgepelt mozdulat kulonben veglegesen eltuntetne
 * valamit, amit meg el kell vegezni.
 *
 * Az ismetlodo emlekezteto ITT sok sor - egy minden alkalomrol. Epp ez az
 * elozmeny ertelme: nem az latszik, hogy "van egy heti riport-emlekeztetom",
 * hanem hogy melyik heten tudtam le, es melyiken nem.
 */
function ElozmenyLista({
  entries,
  betolt,
  meId,
  nameOf,
  onAck,
}: {
  entries: ReminderHistoryEntry[];
  betolt: boolean;
  meId: string;
  nameOf: (id: string) => string;
  onAck: (deliveryId: string, acked: boolean) => void;
}) {
  if (betolt) return <p className="py-6 text-sm text-ink-muted">Betöltés…</p>;

  if (entries.length === 0) {
    return (
      <p className="rounded-control border border-border-soft px-3 py-6 text-center text-xs text-ink-faint">
        Még nem szólt egyetlen emlékeztető sem.
      </p>
    );
  }

  return (
    <ul className="max-h-[26rem] space-y-1 overflow-y-auto">
      {entries.map((entry) => {
        const kesz = Boolean(entry.ackedAt);
        return (
          <li
            key={entry.deliveryId}
            className="flex items-start gap-2.5 rounded-control border border-border-soft px-2.5 py-2"
          >
            <button
              type="button"
              onClick={() => onAck(entry.deliveryId, !kesz)}
              title={kesz ? 'Mégsem intéztem el' : 'Megvan'}
              aria-pressed={kesz}
              className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border transition-colors ${
                kesz
                  ? 'border-done bg-done text-white'
                  : 'border-border-strong text-transparent hover:border-done hover:text-done'
              }`}
            >
              <Icon name="pipa" size="sm" />
            </button>

            <div className="min-w-0 flex-1">
              <p
                className={`whitespace-pre-wrap break-words text-sm ${
                  kesz ? 'text-ink-faint line-through' : 'text-ink'
                }`}
              >
                {entry.text}
              </p>
              <p className="mt-0.5 text-2xs text-ink-faint">
                {new Date(entry.remindAt).toLocaleString('hu-HU', {
                  month: 'short',
                  day: 'numeric',
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {entry.repeatKind !== 'none' && ' · ismétlődő'}
                {entry.creatorId !== meId && ` · ${nameOf(entry.creatorId)} kérte`}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
